import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Headphones, Truck, Code, ArrowRight } from 'lucide-react';

export default function ServicesPage() {
  const { t } = useLanguage();

  const services = [
    {
      icon: Shield,
      title: t('services.licenses.title'),
      desc: t('services.licenses.desc'),
      color: '#0D47A1',
      link: '/licenses',
      tags: ['Microsoft', 'Adobe', '3CX', 'Kaspersky', 'Ekahau'],
      cta: 'Browse Marketplace',
    },
    {
      icon: Headphones,
      title: t('services.support.title'),
      desc: t('services.support.desc'),
      color: '#D32F2F',
      link: '/technical-support',
      tags: ['Microsoft', 'Adobe', '3CX', 'Kaspersky', 'Ekahau'],
      cta: 'Request Support',
    },
    {
      icon: Truck,
      title: t('services.procurement.title'),
      desc: t('services.procurement.desc'),
      color: '#1A237E',
      link: '/procurement',
      tags: ['Industrial Equipment', 'Petroleum Parts', 'Tech Components'],
      cta: 'Request Quote',
    },
    {
      icon: Code,
      title: t('quicknav.hosting'),
      desc: 'Custom web, mobile and desktop applications. Professional hosting at maluzhosting.org.',
      color: '#B71C1C',
      link: '/apps-hosting',
      tags: ['Web Apps', 'Mobile Apps', 'Hosting'],
      cta: 'Get Started',
    },
  ];

  return (
    <>
      <Header />
      <main style={{ paddingTop: 72, minHeight: '100vh' }}>
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #0A1128 0%, #0D47A1 50%, #1A237E 100%)', padding: '80px 24px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#EF5350', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 16 }}>MALUZ TECHNOLOGIES</p>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700, color: 'white', marginBottom: 16, letterSpacing: '-0.03em' }}>
            {t('quicknav.title')}
          </h1>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.7)', maxWidth: 550, margin: '0 auto' }}>
            {t('quicknav.subtitle')}
          </p>
        </div>

        {/* Service Cards */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }} className="services-page-grid">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Link to={s.link} key={i} data-testid={`service-page-card-${i}`}
                  style={{
                    background: 'white', border: '2px solid var(--border)', padding: '40px 36px',
                    display: 'flex', flexDirection: 'column', gap: 16,
                    transition: 'all 0.25s', textDecoration: 'none', cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = s.color;
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}10` }}>
                    <Icon size={28} style={{ color: s.color }} strokeWidth={1.5} />
                  </div>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{s.title}</h2>
                  <p style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--text-secondary)', flex: 1 }}>{s.desc}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {s.tags.map(tag => (
                      <span key={tag} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '3px 10px', background: 'var(--bg-section)', border: '1px solid var(--border)', color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>{tag}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {s.cta} <ArrowRight size={16} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <style>{`
          @media (max-width: 700px) { .services-page-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </main>
      <Footer />
    </>
  );
}
